import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, RadioGroup, Button } from '@rabbit/elements/shared-components';
import classNames from 'classnames';

export interface ModalSelectActionTypeOption {
  value: string;
  label: string;
  description?: string;
  disabled?: boolean;
}

export interface ModalSelectActionTypeProps {
  handleClose: () => void;
  options: ModalSelectActionTypeOption[];
  onContinue: (selected: string) => void;
  childrenClassName?: string;
  className?: string;
}

export function ModalSelectActionType({
  handleClose,
  options,
  onContinue,
  childrenClassName,
  className,
}: ModalSelectActionTypeProps) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<string>(options?.[0]?.value ?? '');

  const selectedOption = options.find((opt) => opt.value === selected);
  const isSelectedDisabled = !!selectedOption?.disabled;

  return (
    <Modal
      kind="generic"
      settings={{
        handleClose,
        title: t('general.selectActionType'),
        childrenClassName: childrenClassName,
      }}
      className={`lg:w-[45.25rem] ${className}`}
    >
      <div className="p-4">
        <RadioGroup
          value={selected}
          onChange={(value) => setSelected(value as string)}
          className="mb-4"
        >
          <div className="space-y-2">
            {options.map((option) => (
              <RadioGroup.Option
                key={option.value}
                value={option.value}
                disabled={option.disabled}
                className={({ checked }) =>
                  classNames(
                    'relative flex rounded-lg border p-3 transition-colors focus:outline-none',
                    {
                      'border-green-900 bg-gray-50':
                        checked && !option.disabled,
                      'cursor-pointer bg-white hover:bg-gray-50':
                        !checked && !option.disabled,
                      'cursor-not-allowed opacity-60': option.disabled,
                    }
                  )
                }
              >
                {({ checked }) => (
                  <div className="flex w-full items-center justify-between">
                    {/* Left side text */}
                    <div className="flex items-center">
                      <div className="text-sm">
                        <RadioGroup.Label
                          as="p"
                          className={classNames('font-medium', {
                            'text-green-900': checked && !option.disabled,
                            'text-gray-900': !checked && !option.disabled,
                          })}
                        >
                          {option.label}
                        </RadioGroup.Label>
                        {option.description && (
                          <RadioGroup.Description
                            as="span"
                            className={classNames('inline text-sm', {
                              'text-green-900': checked && !option.disabled,
                              'text-gray-500': !checked && !option.disabled,
                            })}
                          >
                            {option.description}
                          </RadioGroup.Description>
                        )}
                      </div>
                    </div>

                    {/* Right side radio indicator */}
                    <div
                      className={classNames(
                        'flex h-5 w-5 shrink-0 items-center justify-center rounded-full border',
                        {
                          'border-green-500 bg-green-500':
                            checked && !option.disabled,
                          'border-gray-300': !checked || option.disabled,
                        }
                      )}
                    >
                      {checked && (
                        <div className="h-2 w-2 rounded-full bg-white" />
                      )}
                    </div>
                  </div>
                )}
              </RadioGroup.Option>
            ))}
          </div>
        </RadioGroup>

        {/* Continue button */}
        <div className="mt-6 flex justify-end">
          <Button
            kind="primary"
            className="flex-grow-0"
            disabled={isSelectedDisabled}
            onClick={() => {
              if (!isSelectedDisabled) onContinue(selected);
            }}
          >
            {t('general.continue')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default ModalSelectActionType;
